import React, { useState, useEffect, useCallback } from 'react';
import { TweetContentDto, TweetStatus } from '../../api/bip-api/types';
import TweetContentCard from './TweetContentCard';
import PublishConfirmModal from './PublishConfirmModal';
import OgImagePreview from './OgImagePreview';

interface TweetDetailProps {
  tweet: TweetContentDto;
  isUpdating: boolean;
  isPublishing: boolean;
  onSave: (id: string, contents: string[]) => void;
  onPublish: (id: string, isPersonalTweet?: boolean) => void;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const TweetDetail: React.FC<TweetDetailProps> = ({
  tweet,
  isUpdating,
  isPublishing,
  onSave,
  onPublish,
}) => {
  const [editedContents, setEditedContents] = useState<string[]>(tweet.contents);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);

  const isEditable = tweet.status === TweetStatus.PENDING;
  const hasChanges = JSON.stringify(editedContents) !== JSON.stringify(tweet.contents);

  // Reset edited contents when tweet changes
  useEffect(() => {
    setEditedContents(tweet.contents);
  }, [tweet.id, tweet.contents]);

  const handleContentChange = useCallback((index: number, value: string) => {
    setEditedContents((prev) => {
      const newContents = [...prev];
      newContents[index] = value;
      return newContents;
    });
  }, []);

  const handleAddContent = useCallback(() => {
    setEditedContents((prev) => [...prev, '']);
  }, []);

  const handleDeleteContent = useCallback((index: number) => {
    setEditedContents((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = () => {
    onSave(tweet.id, editedContents);
  };

  const handlePublishClick = () => {
    setShowPublishConfirm(true);
  };

  const handlePublishConfirm = (isPersonalTweet: boolean) => {
    onPublish(tweet.id, isPersonalTweet);
    setShowPublishConfirm(false);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Meta Information */}
      <div className="mb-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-400">Type:</span>{' '}
            <span className="text-white font-medium">{tweet.contentType}</span>
          </div>
          <div>
            <span className="text-gray-400">Status:</span>{' '}
            <span
              className={`font-medium ${
                tweet.status === TweetStatus.PUBLISHED
                  ? 'text-green-400'
                  : 'text-yellow-400'
              }`}
            >
              {tweet.status}
            </span>
          </div>
          <div>
            <span className="text-gray-400">Created:</span>{' '}
            <span className="text-white">{formatDate(tweet.createdAt)}</span>
          </div>
        </div>
        {tweet.status === TweetStatus.PUBLISHED ? (
          <div className="mt-3 p-3 bg-green-900/30 rounded-lg border border-green-600">
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm text-green-400 font-medium">This tweet has been published</span>
            </div>
            {tweet.tweetId && (
              <a
                href={`https://x.com/i/status/${tweet.tweetId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-green-400 hover:text-green-300 hover:underline"
              >
                View on X
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}
          </div>
        ) : (
          tweet.communityId && (
            <div className="mt-3 p-3 bg-blue-900/30 rounded-lg border border-blue-700/50">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
                <span className="text-sm text-blue-400 font-medium">Community에 배포될 콘텐츠</span>
              </div>
              <a
                href={`https://x.com/i/communities/${tweet.communityId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 hover:underline"
              >
                View Community
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          )
        )}
        {tweet.publishedAt && (
          <div className="mt-2 text-sm">
            <span className="text-gray-400">Published:</span>{' '}
            <span className="text-white">{formatDate(tweet.publishedAt)}</span>
          </div>
        )}
      </div>

      {/* OG Image Preview */}
      <OgImagePreview tokenAddress={tweet.tokenAddress} />

      {/* Content Cards */}
      <div className="flex-1 overflow-y-auto space-y-3 mb-4">
        {editedContents.map((content, index) => (
          <TweetContentCard
            key={index}
            index={index}
            content={content}
            isEditable={isEditable}
            canDelete={editedContents.length > 1}
            onChange={(value) => handleContentChange(index, value)}
            onDelete={() => handleDeleteContent(index)}
          />
        ))}
        {/* Add Content Button */}
        {isEditable && (
          <button
            onClick={handleAddContent}
            className="w-full py-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-blue-500 hover:text-blue-400 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Content
          </button>
        )}
      </div>

      {/* Action Buttons */}
      {isEditable && (
        <div className="flex gap-3 pt-4 border-t border-gray-700">
          <button
            onClick={handleSave}
            disabled={!hasChanges || isUpdating}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              hasChanges && !isUpdating
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
          <button
            onClick={handlePublishClick}
            disabled={hasChanges || isUpdating}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
              !hasChanges && !isUpdating
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
            title={hasChanges ? 'Save changes before publishing' : ''}
          >
            Publish
          </button>
        </div>
      )}

      {/* Publish Confirmation Modal */}
      {showPublishConfirm && (
        <PublishConfirmModal
          tweet={tweet}
          isPublishing={isPublishing}
          onConfirm={handlePublishConfirm}
          onCancel={() => setShowPublishConfirm(false)}
        />
      )}
    </div>
  );
};

export default TweetDetail;
